import PropTypes from 'prop-types'

import { TextMessageStatus } from '../../models/message/text-message-status-enum'
import { TextMessageType } from '../../models/message/text-message-type-enum'
import { idPropType } from '../common/id-prop-type'
import { iso8601PropType } from '../common/iso-8601-prop-type'
import { positionPropType } from '../common/position-prop-type'
import { tripModePropType } from '../trip/trip-mode-prop-type'
import { textMessageCommunicatorPropType } from './text-message-communicator-prop-type'

/**
 * A text message.
 *
 * @typedef {Object} TextMessage
 *
 * @property {ISO8601} [acknowledgeTime] - Null, if the message was send from an unit,
 *                                         or was sent to an unit and the unit didn't acknowledged it yet. An ISO-8601 formatted string.
 * @property {string} [addressId]        - The ID of the related address that matches the position of this message.
 *                                         It is not set, when no address matches.
 * @property {ISO8601} creationTime      - The time the text message was created. An ISO-8601 formatted string.
 * @property {ISO8601} [discardedTime]   - The time when the message was discarded from the unit. An ISO-8601 formatted string.
 * @property {ISO8601} lastModifiedTime  - The time when the message was last modified. An ISO-8601 formatted string.
 * @property {number} [odometer]         - The odometer of the unit when the message was received.
 * @property {Id} [orderId]              - Required for type=ORDER_STATUS.
 * @property {Position} [position]       - A geo coordinate on the map.
 * @property {ISO8601} [positionTime]    - The time that is related to the last known position of the vehicle, when the message was received.
 *                                         An ISO-8601 formatted string.
 * @property {ISO8601} [readTime]        - Null, if the message isn't marked as read yet. An ISO-8601 formatted string.
 * @property {TextMessageCommunicator} receiver
 * @property {TextMessageCommunicator} sender
 * @property {number} [speed]           - The speed of the unit when the message was received.
 * @property {TextMessageStatus} status - The lifecycle status of a text message.
 * @property {string} [statusText]      - Only optional for type = TEXT.
 * @property {string} [text]            - Only optional for type = TEXT_STATUS or ORDER_STATUS.
 * @property {Id} textMessageId         - The identifier of the text message.
 * @property {TripModes} tripMode
 * @property {TextMessageType} type
 */

export const textMessagePropType = PropTypes.shape({
    acknowledgeTime: iso8601PropType,
    addressId: idPropType,
    creationTime: iso8601PropType.isRequired,
    discardedTime: iso8601PropType,
    lastModifiedTime: iso8601PropType.isRequired,
    odometer: PropTypes.number,
    orderId: idPropType,
    position: positionPropType,
    positionTime: iso8601PropType,
    readTime: iso8601PropType,
    receiver: textMessageCommunicatorPropType.isRequired,
    sender: textMessageCommunicatorPropType.isRequired,
    speed: PropTypes.number,
    status: PropTypes.oneOf(Object.values(TextMessageStatus)).isRequired,
    statusText: PropTypes.string,
    text: PropTypes.string,
    textMessageId: idPropType.isRequired,
    tripMode: tripModePropType.isRequired,
    type: PropTypes.oneOf(Object.values(TextMessageType)).isRequired
})
